import React, { useEffect, useState, useRef, createElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Input } from '../components/ui/Input';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { FileTextIcon, AlertCircleIcon, CalendarIcon, UserIcon, BuildingIcon, CreditCardIcon, CheckCircleIcon, ClockIcon, XCircleIcon, MailIcon, PhoneIcon, MapPinIcon, LockIcon, PlusIcon, CheckIcon } from 'lucide-react';
// Declare Stripe types
declare global {
  interface Window {
    Stripe: any;
  }
}
// API invoice item type
interface ApiInvoiceItem {
  title: string;
  qty: number;
  price: number;
  amount: number;
  taxable?: number;
}
// Company object from API
interface ApiCompany {
  name: string;
  email?: string;
  phone?: string;
  stripe_public_key?: string;
  logo?: string;
}
// Saved payment method type
interface PaymentMethod {
  id: string;
  card_brand: string;
  card_exp: string;
  card_last_digits: string;
}
// API invoice response type
interface PublicInvoiceData {
  id: string;
  number: string;
  items: ApiInvoiceItem[];
  amount: number;
  paid_amount: number;
  tax: number;
  tax_label?: string;
  fee?: number;
  fee_label?: string;
  shipping_amount: number;
  discount: number;
  status: string;
  created: number;
  sent_date?: number;
  invoice_date?: number;
  customer_id: string;
  customer_name: string;
  customer_organization?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_address_street?: string;
  customer_address_city?: string;
  customer_address_state?: string;
  customer_address_zip?: string;
  customer?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    organization?: string;
    address_street?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  };
  company?: ApiCompany;
  // Legacy fields (fallback)
  company_name?: string;
  company_email?: string;
  subscription_id?: string;
  subscription_number?: string;
  payment_period_days: number | string;
  payment_date?: number;
  // Saved payment methods
  payment_methods?: PaymentMethod[];
}
// Helper to get card brand icon/display name
const getCardBrandDisplay = (brand: string): string => {
  const brands: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay'
  };
  return brands[brand.toLowerCase()] || brand;
};
export function PublicInvoiceView() {
  const {
    hash
  } = useParams<{
    hash: string;
  }>();
  const [invoice, setInvoice] = useState<PublicInvoiceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardError, setCardError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  // Selected payment method: null = new card, string = saved method id
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  // Stripe refs
  const stripeRef = useRef<any>(null);
  const cardElementRef = useRef<any>(null);
  const cardMountedRef = useRef(false);
  // Ref for payment form section (for scroll to payment)
  const paymentFormRef = useRef<HTMLDivElement>(null);
  // Set page title
  useEffect(() => {
    document.title = 'Billing System';
  }, []);
  // Update page title when invoice data loads
  useEffect(() => {
    if (invoice) {
      const companyName = invoice.company?.name || invoice.company_name || '';
      document.title = `${companyName} Invoice # ${invoice.number}`;
    }
  }, [invoice]);
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!hash) {
        setError('Invalid invoice link');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await callBillingAPI('publicGetInvoice', {
          hash
        });
        console.log('Public invoice response:', response);
        setInvoice(response);
        // Set default payment amount to outstanding balance
        const outstanding = (response.amount || 0) - (response.paid_amount || 0);
        setPaymentAmount(outstanding.toFixed(2));
        // If there are saved payment methods, select the first one by default
        if (response.payment_methods && response.payment_methods.length > 0) {
          setSelectedPaymentMethod(response.payment_methods[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch public invoice:', err);
        setError(err instanceof Error ? err.message : 'Failed to load invoice');
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvoice();
  }, [hash]);
  // Load Stripe script and initialize
  useEffect(() => {
    if (!invoice) return;
    const stripePublicKey = invoice.company?.stripe_public_key;
    if (!stripePublicKey) return;
    // Check if payment is needed
    const canPay = ['sent', 'overdue', 'partial_paid'].includes(invoice.status);
    if (!canPay) return;
    // Load Stripe script if not already loaded
    if (!window.Stripe) {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        initializeStripe(stripePublicKey);
      };
      document.body.appendChild(script);
    } else {
      initializeStripe(stripePublicKey);
    }
    return () => {
      // Cleanup card element on unmount
      if (cardElementRef.current) {
        cardElementRef.current.destroy();
        cardElementRef.current = null;
        cardMountedRef.current = false;
      }
    };
  }, [invoice]);
  // Re-mount card element when switching to new card
  useEffect(() => {
    if (selectedPaymentMethod === null && stripeRef.current && !cardMountedRef.current) {
      const stripePublicKey = invoice?.company?.stripe_public_key;
      if (stripePublicKey) {
        // Small delay to ensure DOM element exists
        setTimeout(() => {
          mountCardElement();
        }, 100);
      }
    }
  }, [selectedPaymentMethod, invoice]);
  const mountCardElement = () => {
    if (!stripeRef.current || cardMountedRef.current) return;
    try {
      const elements = stripeRef.current.elements();
      const card = elements.create('card', {
        style: {
          base: {
            color: '#32325d',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
              color: '#aab7c4'
            }
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        }
      });
      const cardElement = document.getElementById('card-element');
      if (cardElement) {
        card.mount('#card-element');
        cardMountedRef.current = true;
        cardElementRef.current = card;
        card.on('change', (event: any) => {
          setCardComplete(event.complete);
          if (event.error) {
            setCardError(event.error.message);
          } else {
            setCardError(null);
          }
        });
        setStripeLoaded(true);
      }
    } catch (err) {
      console.error('Failed to mount card element:', err);
    }
  };
  const initializeStripe = (stripePublicKey: string) => {
    if (stripeRef.current) return;
    try {
      stripeRef.current = window.Stripe(stripePublicKey);
      // Only mount card if no saved payment methods or new card is selected
      if (!invoice?.payment_methods?.length || selectedPaymentMethod === null) {
        const elements = stripeRef.current.elements();
        const card = elements.create('card', {
          style: {
            base: {
              color: '#32325d',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSmoothing: 'antialiased',
              fontSize: '16px',
              '::placeholder': {
                color: '#aab7c4'
              }
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a'
            }
          }
        });
        // Wait for the element to be in DOM
        const mountCard = () => {
          const cardElement = document.getElementById('card-element');
          if (cardElement && !cardMountedRef.current) {
            card.mount('#card-element');
            cardMountedRef.current = true;
            cardElementRef.current = card;
            card.on('change', (event: any) => {
              setCardComplete(event.complete);
              if (event.error) {
                setCardError(event.error.message);
              } else {
                setCardError(null);
              }
            });
            setStripeLoaded(true);
          } else if (!cardMountedRef.current) {
            // Retry mounting if element not found yet
            setTimeout(mountCard, 100);
          }
        };
        mountCard();
      } else {
        // Stripe is loaded but we're using saved method
        setStripeLoaded(true);
      }
    } catch (err) {
      console.error('Failed to initialize Stripe:', err);
      setCardError('Failed to load payment form');
    }
  };
  const handlePayment = async () => {
    if (!invoice) return;
    const amount = parseFloat(paymentAmount);
    const outstanding = invoice.amount - invoice.paid_amount;
    // Validate amount
    if (isNaN(amount) || amount <= 0) {
      setCardError('Please enter a valid payment amount');
      return;
    }
    if (amount > outstanding) {
      setCardError(`Amount cannot exceed outstanding balance of ${formatCurrency(outstanding)}`);
      return;
    }
    setIsProcessingPayment(true);
    setCardError(null);
    try {
      // Check if using saved payment method
      if (selectedPaymentMethod) {
        // Pay with saved method
        await callBillingAPI('publicPayInvoice', {
          hash: hash,
          amount: amount,
          customer_payment_method_id: selectedPaymentMethod
        });
      } else {
        // Pay with new card
        if (!stripeRef.current || !cardElementRef.current) {
          setCardError('Payment form not loaded. Please refresh and try again.');
          setIsProcessingPayment(false);
          return;
        }
        if (!cardHolderName.trim()) {
          setCardError('Please enter the cardholder name');
          setIsProcessingPayment(false);
          return;
        }
        if (!cardComplete) {
          setCardError('Please complete the card details');
          setIsProcessingPayment(false);
          return;
        }
        // Create Stripe Payment method
        const result = await stripeRef.current.createPaymentMethod({
          type: 'card',
          card: cardElementRef.current,
          billing_details: {
            name: cardHolderName
          }
        });
        if (result.error) {
          setCardError(result.error.message);
          setIsProcessingPayment(false);
          return;
        }
        // Call API to process payment
        await callBillingAPI('publicPayInvoice', {
          hash: hash,
          amount: amount,
          stripe_info: {
            method_id: result.paymentMethod.id,
            card_brand: result.paymentMethod.card.brand,
            card_exp: result.paymentMethod.card.exp_month + '/' + result.paymentMethod.card.exp_year,
            card_digits: result.paymentMethod.card.last4
          }
        });
      }
      // Reload page after successful payment
      window.location.reload();
    } catch (err) {
      console.error('Payment failed:', err);
      setCardError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };
  // Handle selecting a saved payment method
  const handleSelectPaymentMethod = (methodId: string | null) => {
    // If switching to new card, unmount existing card element first
    if (methodId === null && cardElementRef.current) {
      cardElementRef.current.destroy();
      cardElementRef.current = null;
      cardMountedRef.current = false;
    }
    setSelectedPaymentMethod(methodId);
    setCardError(null);
  };
  // Handle amount input - ensure it doesn't exceed outstanding
  const handleAmountChange = (value: string) => {
    if (!invoice) return;
    const outstanding = invoice.amount - invoice.paid_amount;
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) {
      setPaymentAmount(value);
      return;
    }
    // Clamp to outstanding amount
    if (numValue > outstanding) {
      setPaymentAmount(outstanding.toFixed(2));
    } else if (numValue < 0) {
      setPaymentAmount('0');
    } else {
      setPaymentAmount(value);
    }
  };
  // Format date from timestamp
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
      icon: React.ReactNode;
    }> = {
      draft: {
        variant: 'default',
        label: 'Draft',
        icon: <FileTextIcon className="w-3 h-3" data-id="element-2065" />
      },
      sent: {
        variant: 'info',
        label: 'Awaiting Payment',
        icon: <ClockIcon className="w-3 h-3" data-id="element-2066" />
      },
      paid: {
        variant: 'success',
        label: 'Paid',
        icon: <CheckCircleIcon className="w-3 h-3" data-id="element-2067" />
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partially Paid',
        icon: <CreditCardIcon className="w-3 h-3" data-id="element-2068" />
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue',
        icon: <AlertCircleIcon className="w-3 h-3" data-id="element-2069" />
      },
      voided: {
        variant: 'default',
        label: 'Voided',
        icon: <XCircleIcon className="w-3 h-3" data-id="element-2070" />
      },
      future: {
        variant: 'info',
        label: 'Scheduled',
        icon: <CalendarIcon className="w-3 h-3" data-id="element-2071" />
      }
    };
    const config = statusConfig[status] || {
      variant: 'default' as const,
      label: status,
      icon: null
    };
    return <Badge variant={config.variant} className="inline-flex items-center gap-1.5 px-3 py-1" data-id="element-2072">
        {config.icon}
        {config.label}
      </Badge>;
  };
  // Loading state
  if (isLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-2073">
        <Card className="max-w-md w-full text-center py-16" data-id="element-2074">
          <div className="inline-block w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" data-id="element-2075"></div>
          <p className="text-gray-600" data-id="element-2076">Loading invoice...</p>
        </Card>
      </div>;
  }
  // Error state
  if (error || !invoice) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-id="element-2077">
        <Card className="max-w-md w-full text-center py-12 px-6" data-id="element-2078">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4" data-id="element-2079">
            <AlertCircleIcon className="w-8 h-8 text-red-600" data-id="element-2080" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2081">
            Invoice Not Found
          </h2>
          <p className="text-gray-600" data-id="element-2082">
            {error || "The invoice you're looking for doesn't exist or the link has expired."}
          </p>
        </Card>
      </div>;
  }
  // Get company info (prefer company object, fallback to legacy fields)
  const companyName = invoice.company?.name || invoice.company_name;
  const companyEmail = invoice.company?.email || invoice.company_email;
  const companyPhone = invoice.company?.phone;
  const companyLogo = invoice.company?.logo;
  const stripePublicKey = invoice.company?.stripe_public_key;
  // Get customer info (check nested customer object, then top-level fields)
  const customerName = (() => {
    // Prefer customer object's first/last name
    if (invoice.customer) {
      const fullName = [invoice.customer.first_name, invoice.customer.last_name].filter(Boolean).join(' ');
      if (fullName) return fullName;
    }
    // Fallback to top-level customer_name
    return invoice.customer_name || 'Unknown';
  })();
  const customerEmail = invoice.customer?.email || '';
  const customerPhone = invoice.customer?.phone || '';
  const customerAddress = (() => {
    // Check nested customer object first
    if (invoice.customer) {
      const nestedParts = [invoice.customer.address_street, invoice.customer.address_city, invoice.customer.address_state, invoice.customer.address_zip].filter(Boolean);
      if (nestedParts.length > 0) return nestedParts.join(', ');
    }
    return '';
  })();
  const customerOrganization = invoice.customer?.organization || '';
  // Calculate totals
  const subtotal = invoice.items?.reduce((sum, item) => sum + item.qty * item.price, 0) || 0;
  const taxAmount = parseFloat(String(invoice.tax)) || 0;
  const taxLabel = invoice.tax_label || 'Tax';
  const feeAmount = parseFloat(String(invoice.fee)) || 0;
  const feeLabel = invoice.fee_label || 'Fee';
  const shippingAmount = parseFloat(String(invoice.shipping_amount)) || 0;
  const discount = parseFloat(String(invoice.discount)) || 0;
  const totalAmount = parseFloat(String(invoice.amount)) || 0;
  const paidAmount = parseFloat(String(invoice.paid_amount)) || 0;
  const balanceDue = totalAmount - paidAmount;
  // Check if payment form should be shown
  const showPaymentForm = ['sent', 'overdue', 'partial_paid'].includes(invoice.status) && balanceDue > 0 && stripePublicKey;
  // Get saved payment methods
  const savedPaymentMethods = invoice.payment_methods || [];
  const hasSavedMethods = savedPaymentMethods.length > 0;
  // Scroll to payment form
  const scrollToPaymentForm = () => {
    paymentFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // Line items table columns
  const lineItemColumns = [{
    key: 'description',
    header: 'Description',
    className: '!whitespace-normal break-words',
    render: (item: ApiInvoiceItem) => <span className="font-medium text-gray-900" data-id="element-2083">{item.title}</span>
  }, {
    key: 'quantity',
    header: 'Qty',
    render: (item: ApiInvoiceItem) => <span className="text-gray-600" data-id="element-2084">{item.qty}</span>
  }, {
    key: 'price',
    header: 'Price',
    render: (item: ApiInvoiceItem) => <span className="text-gray-600" data-id="element-2085">{formatCurrency(item.price)}</span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (item: ApiInvoiceItem) => <span className="font-medium text-gray-900" data-id="element-2086">
          {formatCurrency(item.qty * item.price)}
        </span>
  }];
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4" data-id="element-2087">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-2088">
        {/* Invoice Header Card */}
        <Card className="overflow-hidden" data-id="element-2089">
          {/* Status Banner */}
          <div className={`px-6 py-3 ${invoice.status === 'paid' ? 'bg-green-50 border-b border-green-100' : invoice.status === 'overdue' ? 'bg-red-50 border-b border-red-100' : 'bg-blue-50 border-b border-blue-100'}`} data-id="element-2090">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-2091">
              <div className="flex items-center gap-3" data-id="element-2092">
                <FileTextIcon className={`w-5 h-5 flex-shrink-0 ${invoice.status === 'paid' ? 'text-green-600' : invoice.status === 'overdue' ? 'text-red-600' : 'text-blue-600'}`} data-id="element-2093" />
                <span className="font-semibold text-gray-900" data-id="element-2094">
                  Invoice #{invoice.number}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-2095">
                {getStatusBadge(invoice.status)}
                {showPaymentForm && <Button onClick={scrollToPaymentForm} size="sm" leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-2097" />} data-id="element-2096">
                    Pay Now
                  </Button>}
              </div>
            </div>
          </div>

          <div className="p-6" data-id="element-2098">
            {/* Company Logo - prominent at top */}
            {companyLogo && <div className="mb-6" data-id="element-2099">
                <img src={`data:image/png;base64,${companyLogo}`} alt={companyName || 'Company'} className="max-w-xs max-h-24 object-contain" data-id="element-2100" />
              </div>}

            {/* Company & Customer Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-2101">
              {/* From (Company) */}
              {companyName && <div data-id="element-2102">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2" data-id="element-2103">
                    <BuildingIcon className="w-4 h-4" data-id="element-2104" />
                    From
                  </div>
                  <div className="text-gray-900" data-id="element-2105">
                    <p className="font-semibold text-lg" data-id="element-2106">{companyName}</p>
                    {companyEmail && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-2107">
                        <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2108" />
                        {companyEmail}
                      </p>}
                    {companyPhone && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-2109">
                        <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2110" />
                        {companyPhone}
                      </p>}
                  </div>
                </div>}

              {/* To (Customer) */}
              <div data-id="element-2111">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2" data-id="element-2112">
                  <UserIcon className="w-4 h-4" data-id="element-2113" />
                  Bill To
                </div>
                <div className="text-gray-900" data-id="element-2114">
                  <p className="font-semibold text-lg" data-id="element-2115">{customerName}</p>
                  {customerOrganization && customerOrganization.trim() !== customerName.trim() && <p className="text-sm text-gray-500" data-id="element-2116">
                        {customerOrganization}
                      </p>}
                  {customerEmail && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-2117">
                      <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2118" />
                      {customerEmail}
                    </p>}
                  {customerPhone && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-2119">
                      <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2120" />
                      {customerPhone}
                    </p>}
                  {customerAddress && <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1" data-id="element-2121">
                      <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-2122" />
                      {customerAddress}
                    </p>}
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-6" data-id="element-2123">
              <div data-id="element-2124">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2125">
                  Invoice Date
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-2126">
                  {invoice.invoice_date ? formatDate(invoice.invoice_date) : invoice.created ? formatDate(invoice.created) : '—'}
                </p>
              </div>
              {invoice.sent_date && <div data-id="element-2127">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2128">
                    Sent Date
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-2129">
                    {formatDate(invoice.sent_date)}
                  </p>
                </div>}
              <div data-id="element-2130">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2131">
                  Payment Terms
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-2132">
                  {parseInt(String(invoice.payment_period_days)) === 0 ? 'Due on Receipt' : `Net ${parseInt(String(invoice.payment_period_days))} days`}
                </p>
              </div>
              {invoice.payment_date && Number(invoice.payment_date) > 0 && <div data-id="element-2133">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2134">
                    Paid On
                  </p>
                  <p className="text-sm font-semibold text-green-600 mt-1" data-id="element-2135">
                    {formatDate(Number(invoice.payment_date))}
                  </p>
                </div>}
            </div>
          </div>
        </Card>

        {/* Line Items */}
        <Card padding="none" data-id="element-2136">
          <div className="p-6 border-b border-gray-200" data-id="element-2137">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-2138">Items</h2>
          </div>
          <Table columns={lineItemColumns} data={invoice.items || []} keyExtractor={(_, index) => String(index)} emptyMessage="No items" data-id="element-2139" />

          {/* Totals */}
          <div className="p-6 bg-gray-50 border-t border-gray-200" data-id="element-2140">
            <div className="max-w-xs ml-auto space-y-2" data-id="element-2141">
              <div className="flex justify-between text-sm" data-id="element-2142">
                <span className="text-gray-600" data-id="element-2143">Subtotal</span>
                <span className="text-gray-900" data-id="element-2144">
                  {formatCurrency(subtotal)}
                </span>
              </div>
              {taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2145">
                  <span className="text-gray-600" data-id="element-2146">{taxLabel}</span>
                  <span className="text-gray-900" data-id="element-2147">
                    {formatCurrency(taxAmount)}
                  </span>
                </div>}
              {feeAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2148">
                  <span className="text-gray-600" data-id="element-2149">{feeLabel}</span>
                  <span className="text-gray-900" data-id="element-2150">
                    {formatCurrency(feeAmount)}
                  </span>
                </div>}
              {shippingAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2151">
                  <span className="text-gray-600" data-id="element-2152">Shipping</span>
                  <span className="text-gray-900" data-id="element-2153">
                    {formatCurrency(shippingAmount)}
                  </span>
                </div>}
              {discount > 0 && <div className="flex justify-between text-sm" data-id="element-2154">
                  <span className="text-gray-600" data-id="element-2155">Discount</span>
                  <span className="text-green-600" data-id="element-2156">
                    -{formatCurrency(discount)}
                  </span>
                </div>}
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200" data-id="element-2157">
                <span className="text-gray-900" data-id="element-2158">Total</span>
                <span className="text-gray-900" data-id="element-2159">
                  {formatCurrency(totalAmount)}
                </span>
              </div>
              {paidAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2160">
                  <span className="text-gray-600" data-id="element-2161">Paid</span>
                  <span className="text-green-600" data-id="element-2162">
                    -{formatCurrency(paidAmount)}
                  </span>
                </div>}
              {balanceDue > 0 && <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200" data-id="element-2163">
                  <span className="text-gray-900" data-id="element-2164">Balance Due</span>
                  <span className="text-red-600" data-id="element-2165">
                    {formatCurrency(balanceDue)}
                  </span>
                </div>}
            </div>
          </div>
        </Card>

        {/* Payment Status Card */}
        {invoice.status === 'paid' ? <Card className="bg-green-50 border-green-200" data-id="element-2166">
            <div className="flex items-center gap-4" data-id="element-2167">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2168">
                <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-2169" />
              </div>
              <div data-id="element-2170">
                <h3 className="font-semibold text-green-900" data-id="element-2171">
                  {totalAmount === 0 ? 'No Payment Required' : 'Payment Complete'}
                </h3>
                <p className="text-sm text-green-700" data-id="element-2172">
                  {totalAmount === 0 ? 'This invoice has a zero balance. No payment is required.' : 'This invoice has been paid in full. Thank you for your payment!'}
                </p>
              </div>
            </div>
          </Card> : invoice.status === 'overdue' ? <Card className="bg-red-50 border-red-200" data-id="element-2173">
            <div className="flex items-center gap-4" data-id="element-2174">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center" data-id="element-2175">
                <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2176" />
              </div>
              <div data-id="element-2177">
                <h3 className="font-semibold text-red-900" data-id="element-2178">Payment Overdue</h3>
                <p className="text-sm text-red-700" data-id="element-2179">
                  This invoice is past due. Please make payment as soon as
                  possible.
                </p>
              </div>
            </div>
          </Card> : invoice.status === 'sent' || invoice.status === 'partial_paid' ? <Card className="bg-blue-50 border-blue-200" data-id="element-2180">
            <div className="flex items-center gap-4" data-id="element-2181">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-2182">
                <CreditCardIcon className="w-6 h-6 text-blue-600" data-id="element-2183" />
              </div>
              <div data-id="element-2184">
                <h3 className="font-semibold text-blue-900" data-id="element-2185">
                  Payment Required
                </h3>
                <p className="text-sm text-blue-700" data-id="element-2186">
                  {balanceDue > 0 ? `Amount due: ${formatCurrency(balanceDue)}` : 'Please complete payment for this invoice.'}
                </p>
              </div>
            </div>
          </Card> : null}

        {/* Payment Form */}
        {showPaymentForm && <div ref={paymentFormRef} data-id="element-2187">
            <Card data-id="element-2188">
              <div className="flex items-center gap-3 mb-6" data-id="element-2189">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-2190">
                  <CreditCardIcon className="w-5 h-5 text-blue-600" data-id="element-2191" />
                </div>
                <div data-id="element-2192">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-2193">
                    Make a Payment
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-2194">
                    Secure payment powered by Stripe
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-2195">
                {/* Payment Amount */}
                <div data-id="element-2196">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2197">
                    Payment Amount
                  </label>
                  <div className="relative" data-id="element-2198">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-2199">
                      $
                    </span>
                    <input type="number" min="0.01" max={balanceDue} step="0.01" value={paymentAmount} onChange={e => handleAmountChange(e.target.value)} className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" placeholder="0.00" data-id="element-2200" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2201">
                    Outstanding balance: {formatCurrency(balanceDue)}
                  </p>
                </div>

                {/* Payment Method Selection */}
                {hasSavedMethods && <div data-id="element-2202">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2203">
                      Payment Method
                    </label>
                    <div className="space-y-2" data-id="element-2204">
                      {/* Saved payment methods */}
                      {savedPaymentMethods.map(method => <button key={method.id} type="button" onClick={() => handleSelectPaymentMethod(method.id)} className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-colors ${selectedPaymentMethod === method.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-2205">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === method.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} data-id="element-2206">
                            {selectedPaymentMethod === method.id && <CheckIcon className="w-3 h-3 text-white" data-id="element-2207" />}
                          </div>
                          <CreditCardIcon className="w-5 h-5 text-gray-400" data-id="element-2208" />
                          <div className="flex-1 text-left" data-id="element-2209">
                            <span className="font-medium text-gray-900" data-id="element-2210">
                              {getCardBrandDisplay(method.card_brand)} ••••{' '}
                              {method.card_last_digits}
                            </span>
                            <span className="text-sm text-gray-500 ml-2" data-id="element-2211">
                              Exp {method.card_exp}
                            </span>
                          </div>
                        </button>)}

                      {/* New card option */}
                      <button type="button" onClick={() => handleSelectPaymentMethod(null)} className={`w-full flex items-center gap-3 p-3 border rounded-lg transition-colors ${selectedPaymentMethod === null ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-2212">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === null ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`} data-id="element-2213">
                          {selectedPaymentMethod === null && <CheckIcon className="w-3 h-3 text-white" data-id="element-2214" />}
                        </div>
                        <PlusIcon className="w-5 h-5 text-gray-400" data-id="element-2215" />
                        <span className="font-medium text-gray-900" data-id="element-2216">
                          Use a new card
                        </span>
                      </button>
                    </div>
                  </div>}

                {/* New Card Form - only show if no saved methods or new card is selected */}
                {(selectedPaymentMethod === null || !hasSavedMethods) && <>
                    {/* Cardholder Name */}
                    <div data-id="element-2217">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2218">
                        Cardholder Name
                      </label>
                      <input type="text" value={cardHolderName} onChange={e => setCardHolderName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900" placeholder="Name on card" data-id="element-2219" />
                    </div>

                    {/* Stripe Card Element */}
                    <div data-id="element-2220">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2221">
                        Card Details
                      </label>
                      <div id="card-element" className="px-4 py-3 border border-gray-300 rounded-lg bg-white min-h-[44px]" data-id="element-2222" />
                      {!stripeLoaded && <div className="flex items-center gap-2 mt-2 text-sm text-gray-500" data-id="element-2223">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2224"></div>
                          Loading payment form...
                        </div>}
                    </div>
                  </>}

                {/* Error Message */}
                {cardError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-id="element-2225">
                    <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2226" />
                    <p className="text-sm text-red-600" data-id="element-2227">{cardError}</p>
                  </div>}

                {/* Submit Button */}
                <Button onClick={handlePayment} disabled={isProcessingPayment || !selectedPaymentMethod && !stripeLoaded} isLoading={isProcessingPayment} className="w-full" leftIcon={!isProcessingPayment ? <LockIcon className="w-4 h-4" data-id="element-2229" /> : undefined} data-id="element-2228">
                  {isProcessingPayment ? 'Processing...' : `Pay ${formatCurrency(parseFloat(paymentAmount) || 0)}`}
                </Button>

                {/* Security Note */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-2230">
                  <LockIcon className="w-3.5 h-3.5" data-id="element-2231" />
                  <span data-id="element-2232">Your payment is secure and encrypted</span>
                </div>
              </div>
            </Card>
          </div>}

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 py-4" data-id="element-2233">
          <p data-id="element-2234">
            Questions about this invoice? Contact {companyEmail || 'the sender'}
            .
          </p>
        </div>
      </div>
    </div>;
}